import { useState } from 'react';
import moment from 'moment';
import Select, { SingleValue } from 'react-select';
import DatePicker from 'react-datepicker';
import Button from '../Shared/Button';
import Loader from '../../components/Shared/Loader';
import closeIcon from '../../assets/images/close-icon.png';
import { getSalesExcel } from '../../services/SalesService';

import '../Global/Popup/popup.scss';
import './index.scss';

type ChannelOption = {
  label: string;
  value: string;
};

type ExportPopupProps = {
  partnerId: string | null;
  channelOptions: ChannelOption[];
  selectedBusinessModelId: string;
  currentDate: Date;
  onClose: () => void;
  CustomDatepickerInput: React.ForwardRefExoticComponent<any>;
  categoryId?: string;
};

const ExportPopup = ({
  partnerId,
  channelOptions,
  selectedBusinessModelId,
  currentDate,
  onClose,
  CustomDatepickerInput,
  categoryId
}: ExportPopupProps) => {
  const [showLoader, setShowLoader] = useState(false);

  const [selectedChannel, setSelectedChannel] = useState<ChannelOption>({
    label: 'All',
    value: 'all',
  });

  const yearsBack = selectedBusinessModelId === "10001" && categoryId === "10003" ? 1 : 2;

  const customDate: any = new Date()?.setDate(new Date()?.getDate() - 1);

  const [dateRange, setDateRange] = useState({
    fromDate: new Date(customDate)?.setMonth(new Date(customDate)?.getMonth() - 1),
    toDate: customDate,
  });
  const portfolioBrand: any = JSON.parse(sessionStorage.getItem('portfolioBrand')!);

  const handleDateChange = (date: Date | null, type: 'from' | 'to') => {
    if (!date) return;

    setDateRange((prev) => ({
      ...prev,
      [type === 'from' ? 'fromDate' : 'toDate']: date,
    }));
  };

  const handleExport = async () => {
    try {
      setShowLoader(true);
      const from = moment(dateRange.fromDate).format('DD MMM YYYY');
      const to = moment(dateRange.toDate).format('DD MMM YYYY');

      const blob = await getSalesExcel(
        partnerId,
        moment(dateRange.fromDate).format('YYYY-MM-DD'),
        moment(dateRange.toDate).format('YYYY-MM-DD'),
        portfolioBrand.value,
        selectedBusinessModelId,
        selectedChannel.label,
        categoryId
      );

      if (blob) {
        const fileName = `Sales_${from}_${to}_${selectedChannel.label}.xlsx`;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <div className="popup-backdrop">
      <div className="export-popup">
        {/* HEADER */}
        <div className="export-popup--header Popup-header">
          <h6>
            Export data
            <span onClick={onClose}>
              <img src={closeIcon} alt="close" />
            </span>
          </h6>
        </div>

        {/* BODY */}
        <div className="export-popup--body">
          {/* Channel */}
          <div className="field">
            <Select
              value={selectedChannel}
              onChange={(v: SingleValue<ChannelOption>) =>
                setSelectedChannel(v ?? { label: 'All', value: 'all' })
              }
              options={channelOptions}
              isSearchable
              placeholder="Channel"
            />
          </div>

          {/* From */}
          <div className="field">
            <DatePicker
              selected={dateRange.fromDate}
              onChange={(d) => handleDateChange(d, 'from')}
              minDate={new Date(new Date().setFullYear(new Date().getFullYear() - yearsBack))}
              maxDate={currentDate}
              customInput={<CustomDatepickerInput label="From" />}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
            />
          </div>

          {/* To */}
          <div className="field">
            <DatePicker
              selected={dateRange.toDate}
              onChange={(d) => handleDateChange(d, 'to')}
              minDate={dateRange.fromDate}
              maxDate={currentDate}
              customInput={<CustomDatepickerInput label="To" />}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
            />
          </div>
        </div>

        {/* FOOTER */}
        <div className="export-popup--footer">
          <Button
            className="Button Button--transparentBlack"
            onClick={handleExport}
            data-testid="export"
          >
            Export
          </Button>
        </div>
      </div>

      {showLoader && <Loader />}
    </div>
  );
};

export default ExportPopup;